/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState } from "react";
import { IonInput } from "@ionic/react";
import "./PhoneFormate.css"; // Ensure you have a CSS file for styles

import { AllCountryCodes } from "../../utils/AllCountryCodes";
import SelectionModal from "./popups/SelectionModal";

interface UserInfoProps {
  CountryCode: string;
  phone: string;
  isCodeModalOpen?: (value: boolean) => void;
  setPhone: (value: string) => void;
  setPopup?: (value: boolean) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  setCountryCode: (value: string) => void;
  handlePhoneInput?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface PhoneFormatterProps extends UserInfoProps {
  setIsFormated: (value: boolean) => void; // Optional setter for validation state
}

export const PhoneFormatter: React.FC<PhoneFormatterProps> = ({
  CountryCode,
  phone,
  onBlur,
  isCodeModalOpen,
  setPhone,
  setCountryCode,
  setPopup,
  setIsFormated,
}) => {
  const detectCountryCode = (value: string) => {
    if (!value.startsWith("+")) return null;

    const codes = Object.keys(AllCountryCodes).sort(
      (a, b) => b.length - a.length
    );

    for (const code of codes) {
      if (value.startsWith(code)) {
        return code;
      }
    }
    return null;
  };

  const [codeModal, setCodeModal] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([]);

  useEffect(() => {
    if (codeModal) {
      if (setPopup) setPopup(false);
    }
  }, [codeModal]);

  useEffect(() => {
    if (isCodeModalOpen) isCodeModalOpen(codeModal);
  }, [codeModal]);
  const validatePhoneNumber = (value: string, CountryCode: string): boolean => {
    const detectedCountryCode = CountryCode;

    if (
      !detectedCountryCode ||
      !AllCountryCodes[detectedCountryCode as keyof typeof AllCountryCodes]
    ) {
      return false;
    }

    const typedCountryCode = CountryCode as keyof typeof AllCountryCodes;
    const country = AllCountryCodes[typedCountryCode];

    const countryCodeDigits = CountryCode.replace("+", "");
    const allDigits = value.replace(/[^\d]/g, "");
    const phoneDigits = allDigits;

    if (country.maxLength) {
      return phoneDigits.length === country.maxLength;
    }
    return false;
  };

  const formatByPattern = (CountryCode: string, digits: string) => {
    if (digits.length === 0) {
      return "";
    }
    const country =
      AllCountryCodes[CountryCode as keyof typeof AllCountryCodes];
    if (!country) return digits;

    // Special handling for US/Canada - return only formatted number without country code
    if (CountryCode === "+1") {
      if (digits.length <= 3) {
        return `(${digits}`;
      } else if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
          6
        )}`;
      }
    }

    // Parse the pattern to understand how to format
    const pattern = country.pattern;
    const digitPattern = pattern.replace(CountryCode, "").trim();

    // Parse pattern to extract formatting groups
    const formatGroups: {
      type: "digits" | "separator";
      count?: number;
      char?: string;
    }[] = [];
    let currentGroup = "";
    for (let i = 0; i < digitPattern.length; i++) {
      const char = digitPattern[i];
      if (char === "#") {
        currentGroup += "#";
      } else if (char === " " || char === "-" || char === "(" || char === ")") {
        if (currentGroup) {
          formatGroups.push({ type: "digits", count: currentGroup.length });
          currentGroup = "";
        }
        if (char !== " ") {
          formatGroups.push({ type: "separator", char: char });
        } else {
          formatGroups.push({ type: "separator", char: " " });
        }
      }
    }
    if (currentGroup) {
      formatGroups.push({ type: "digits", count: currentGroup.length });
    }

    // Apply formatting based on parsed groups - return only formatted number
    let result = "";
    let digitPos = 0;
    let isFirstGroup = true;

    for (const group of formatGroups) {
      if (group.type === "digits" && typeof group.count === "number") {
        const groupDigits = digits.slice(digitPos, digitPos + group.count);
        if (groupDigits.length > 0) {
          // Don't add space before first group, but add space before subsequent digit groups
          if (
            !isFirstGroup &&
            result &&
            !result.endsWith(" ") &&
            !result.endsWith("(") &&
            !result.endsWith("-")
          ) {
            result += " ";
          }
          result += groupDigits;
          digitPos += group.count;
          isFirstGroup = false;
        }
        if (digitPos >= digits.length) break;
      } else if (group.type === "separator" && digitPos < digits.length) {
        const remainingDigits = digits.slice(digitPos);
        if (remainingDigits.length > 0 && group.char) {
          result += group.char;
        }
      }
    }

    return result;
  };

  const formatPhoneNumber = (value: string, CountryCode: string) => {
    const cleaned = value.replace(/[^\d+]/g, "");
    return formatByPattern(CountryCode, cleaned);
  };

  const handlePhoneInput = (e: any) => {
    const value = e.detail.value || "";
    const formatted = formatPhoneNumber(value, CountryCode);
    setPhone(formatted);
    setCountryCode(CountryCode);
  };

  useEffect(() => {
    if (setIsFormated) {
      const isValid = validatePhoneNumber(phone, CountryCode);
      setIsFormated(isValid);
    }
  }, [phone, setIsFormated]);

  const handleCountryCodeSelection = (e: any, fieldName: string) => {
    const selectedCode = e.detail.value;
    if (selectedCode) {
      setCountryCode(selectedCode);
      setCodeModal(false);

      if (phone) {
        const cleanedInput = phone.replace(/[^\d]/g, "");
        if (cleanedInput) {
          const formatted = formatPhoneNumber(cleanedInput, selectedCode);
          setPhone(formatted);
          setPhoneNumbers([formatted]);
        }
      }
    }
  };

  return (
    <>
      <div
        style={{
          borderRadius: "15px",
          transition: "filter 0.3s",
        }}
      >
        {codeModal && (
          <SelectionModal
            isOpen={codeModal}
            onClose={() => setCodeModal(false)}
            title="Select Country Code"
            subtitle="Choose your country code"
            options={Object.entries(AllCountryCodes).map(([code, info]) => ({
              id: code,
              label: `${code} ${info.name}`,
              selected: code === CountryCode,
            }))}
            showSearch={true}
            fieldName="country_code"
            buttonText="Choose"
            onSave={handleCountryCodeSelection}
            multiSelect={false}
            allowCustomOptions={false}
          />
        )}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            borderRadius: "12px",
            width: "100% !important",
            maxWidth: "100%", // adjust max width as needed
            gap: "8px", // spacing between code and phone
          }}
        >
          {/* Country Code Selector */}
          <div
            style={{
              position: "relative",
              width: "90px",
              minWidth: "70px",
              display: "flex",
              height: "20px",
              alignItems: "center",
              cursor: "pointer",
            }}
            onClick={() => setCodeModal(true)}
          >
            <div
              style={{
                width: "80%",
                height: "45px",
                background: "#fff",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <IonInput
                style={{
                  width: "100%",
                  height: "43px !important",
                  textAlign: "left",
                  border: "1px solid #d0d4d8ff",
                  borderRadius: "10px",
                  padding: "0 8px",
                  outline: "none",
                  color: "#586670",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: "16px",
                }}
                value={CountryCode || CountryCode}
              />
              <span
                style={{
                  position: "absolute",
                  right: "8px",
                  paddingLeft: "6px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "12px",
                  color: "#424040",
                  pointerEvents: "none",
                }}
              >
                ▼
              </span>
            </div>
          </div>
          <div style={{ marginLeft: "-20px", flex: 1 }}>
            {/* Phone Number Input */}
            <IonInput
              maxlength={(() => {
                const country =
                  AllCountryCodes[CountryCode as keyof typeof AllCountryCodes];
                if (!country) return undefined;
                // Count non-digit chars in the pattern (excluding country code)
                const pattern = country.pattern.replace(CountryCode, "").trim();
                const extraChars = (pattern.match(/[^#]/g) || []).length;
                return (country.maxLength || 0) + extraChars;
              })()}
              className="native-wrapper"
              style={{
                flex: 1,
                width: "100% !important",
                height: "30px",
                fontSize: "16px",

                border: "1px solid #d0d4d8ff",
                borderRadius: "10px",
                backgroundColor: "white",
                padding: "0 12px",
                background: "#fff",
                color: "#586670",
                outline: "none",
              }}
              placeholder="Phone Number"
              value={phone}
              onIonBlur={(e) => {
                if (codeModal) {
                  setCodeModal(false);
                } else if (onBlur) {
                  onBlur(e as any);
                }
              }}
              onIonInput={handlePhoneInput}
              type="tel"
            />
          </div>
        </div>
      </div>
    </>
  );
};
export default PhoneFormatter;
